import { Box, Typography } from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { Booking } from "src/business/booking";
import { BookingStatusBadge } from "src/components/Main/Pages/Bookings/components/BookingStatusBadge";
import {
  colorBorder,
  colorTextMuted,
  colorTextPrimary,
  iconBgInfo,
  iconColorInfo,
  radiusChip,
} from "src/design/tokens";

interface TodaysBookingRowProps {
  booking: Booking;
  /** Drops the separator under the last row of the list. */
  isLast: boolean;
}

/** "Amine Benali" → "AB"; falls back to a single letter for one-word names. */
const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

const Meta = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    {icon}
    <Typography sx={{ fontSize: "12.5px", color: colorTextMuted }}>
      {label}
    </Typography>
  </Box>
);

export const TodaysBookingRow = ({ booking, isLast }: TodaysBookingRowProps) => {
  const offer = booking.dealContent ?? booking.eventContent;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.5,
        borderBottom: isLast ? "none" : `1px solid ${colorBorder}`,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: radiusChip,
          backgroundColor: iconBgInfo,
          color: iconColorInfo,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          fontSize: "13px",
        }}
      >
        {initials(booking.userName)}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: "14.5px", fontWeight: 600, color: colorTextPrimary }}
          noWrap
        >
          {booking.userName}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 0.25,
          }}
        >
          <Meta
            icon={
              <PeopleOutlineIcon sx={{ fontSize: 14, color: colorTextMuted }} />
            }
            label={String(booking.numberOfSeats)}
          />
          {booking.phone && (
            <Meta
              icon={
                <PhoneOutlinedIcon sx={{ fontSize: 14, color: colorTextMuted }} />
              }
              label={booking.phone}
            />
          )}
          {offer && (
            <Meta
              icon={
                <LocalOfferOutlinedIcon
                  sx={{ fontSize: 14, color: colorTextMuted }}
                />
              }
              label={offer}
            />
          )}
        </Box>
      </Box>

      <BookingStatusBadge status={booking.bookingStatus} />

      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 700,
          color: colorTextPrimary,
          minWidth: 48,
          textAlign: "right",
        }}
      >
        {booking.bookingHour}
      </Typography>
    </Box>
  );
};
